import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import Isvg from 'react-inlinesvg';
import { parse, addMinutes, format } from 'date-fns';
import _last from 'lodash/last';

import { bem } from 'helpers/class-name';
import { ICONS } from 'constants/icons';
import { reduxFormPropTypes } from 'constants/propTypes';
import { Input, TimePicker, InputLabel } from 'components/Form';
import Message from 'components/Message';
import messages from '../../ScheduleForm.messages';
import './StartTimeDurationInput.scss';

const { block, element } = bem('start-time-duration-input');

const DurationInput = ({ currentFirstDuration, isReadOnly, input, ...rest }) => {
  let props = {};

  if (isReadOnly) {
    props = {
      disabled: isReadOnly,
      input: {
        ...input,
        onChange: () => {},
        value: currentFirstDuration,
      },
    };
  }

  return <Input input={input} {...rest} {...props} />;
};

DurationInput.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string,
  }),
  currentFirstDuration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isReadOnly: PropTypes.bool,
};

export const getNextTime = (fields, currentFirstDuration) => {
  const roundedUpDuration = Math.ceil(currentFirstDuration / 5) * 5;
  const minutesToAdd = roundedUpDuration || 15;
  const allFields = fields.getAll();
  const lastField = _last(allFields);

  if (!lastField || !lastField.time) return '';

  const lastFieldTime = parse(lastField.time, 'HH:mm', new Date());
  const nextTime = format(addMinutes(lastFieldTime, minutesToAdd), 'HH:mm');

  if (allFields.find(el => el.time === nextTime) || nextTime === '00:00') return '';

  return nextTime;
};

const StartTimeDurationInput = ({ fields, currentFirstDuration }) => {
  const handleRemove = idx => () => fields.remove(idx);
  const handlePush = () =>
    fields.push({ time: getNextTime(fields, currentFirstDuration), durationInMinutes: currentFirstDuration });
  return (
    <div className={block()}>
      <div className="grid">
        <div className="grid__item 5/11">
          <InputLabel message={messages.startTime} />
        </div>
        <div className="grid__item 5/11">
          <InputLabel message={messages.duration} />
        </div>
      </div>
      {fields.map((field, idx) => {
        const isReadOnly = idx !== 0;
        return (
          <div className={element('fields', 'grid')} key={field}>
            <div className={element('field', 'grid__item 5/11')} data-cypress="start-time-input">
              <Field name={`${field}.time`} component={TimePicker} />
            </div>
            <div className={element('field', 'grid__item 5/11')} data-cypress="duration-input">
              <Field
                parse={val => val && Number(val)}
                name={`${field}.durationInMinutes`}
                type="number"
                component={DurationInput}
                isReadOnly={isReadOnly}
                currentFirstDuration={currentFirstDuration}
                className={element('duration-input')}
              />
            </div>
            {isReadOnly && (
              <div className={element('remove-button', 'grid__item 1/11')} data-cypress="remove-start-time-button">
                <button type="button" className="class-floors__delete" onClick={handleRemove(idx)}>
                  <Isvg uniquifyIDs={false} src={ICONS.crossSmall} />
                </button>
              </div>
            )}
          </div>
        );
      })}
      <div className={element('footer')} data-cypress="add-more-start-times-button">
        <Message as="button" onClick={handlePush} message={messages.addMoreStartTimes} />
      </div>
    </div>
  );
};

StartTimeDurationInput.propTypes = {
  fields: reduxFormPropTypes.fieldArray.fields,
  currentFirstDuration: PropTypes.number,
};

export default StartTimeDurationInput;
